import { useEffect, useRef, useState } from 'react';

import { useTheme } from '@/state/ThemeContext';

/**
 * Escenografía de fondo.
 *
 * Es la capa que hace que estar dentro de la aplicación se parezca a estar
 * dentro de una escena y no delante de un formulario: un guerrero enorme al
 * fondo que se desplaza con el scroll, brasas subiendo, un haz de luz que
 * barre muy despacio y un viñeteado que cierra los bordes.
 *
 * Tres reglas la mantienen del lado del decorado y no del ruido:
 *
 *   - Opacidad baja de verdad. Si compite con el texto, deja de ser fondo.
 *   - Solo `transform` y `opacity`, que van en la GPU. Nada que obligue al
 *     navegador a recalcular la maquetación en cada fotograma.
 *   - `pointer-events: none` en todo: no intercepta ni un clic.
 *
 * El paralaje escucha el scroll con `requestAnimationFrame`, así que se
 * actualiza como mucho una vez por fotograma aunque lleguen cien eventos.
 *
 * Con `prefers-reduced-motion` no se monta.
 */
export function CinematicBackdrop() {
  const { reducedMotion } = useTheme();
  const layerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (reducedMotion || !mounted) return;
    const node = layerRef.current;
    if (!node) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        // El fondo se mueve a un tercio de lo que se mueve la página: es esa
        // diferencia de velocidad la que da la sensación de profundidad.
        node.style.transform = `translate3d(0, ${window.scrollY * -0.28}px, 0)`;
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [reducedMotion, mounted]);

  if (reducedMotion) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
      aria-hidden="true"
    >
      {/*
        --- Capa lejana: el guerrero, con paralaje ------------------------
        La opacidad es baja de verdad y baja MAS en pantalla estrecha: a
        0.055 y 130vh de alto competia con el texto de las tarjetas en un
        movil, y un fondo que se lee es un fondo que estorba.
      */}
      <div
        ref={layerRef}
        className="absolute inset-x-0 -top-24 flex justify-center will-change-transform"
      >
        <HopliteSilhouette className="h-[85vh] w-auto opacity-[0.025] animate-drift-slow sm:h-[120vh] sm:opacity-[0.04]" />
      </div>

      {/* Velo sobre el guerrero: lo hunde en el fondo y deja el texto limpio. */}
      <div className="absolute inset-0 [background:linear-gradient(180deg,rgb(var(--cp-bg)/0.55),rgb(var(--cp-bg)/0.8))]" />

      {/* --- Haz de luz que barre la escena ------------------------------ */}
      <div className="absolute inset-0 animate-sweep-light [background:linear-gradient(105deg,transparent_35%,rgb(var(--cp-gold)/0.05)_50%,transparent_65%)]" />

      {/* --- Brasas ------------------------------------------------------- */}
      {EMBERS.map((e, i) => (
        <span
          key={i}
          className="absolute block rounded-full bg-gold/50 animate-ember-float"
          style={{
            left: `${e.left}%`,
            bottom: '-2%',
            width: e.size,
            height: e.size,
            animationDelay: `${e.delay}ms`,
            animationDuration: `${e.duration}ms`,
          }}
        />
      ))}

      {/* --- Viñeteado: cierra los bordes como un objetivo de cine -------- */}
      <div className="absolute inset-0 [background:radial-gradient(ellipse_at_center,transparent_45%,rgb(var(--cp-obsidian)/0.55)_100%)]" />

      {/* --- Grano de película -------------------------------------------- */}
      <div className="absolute inset-0 animate-grain opacity-[0.035] [background-image:radial-gradient(circle_at_15%_25%,white_0.5px,transparent_0.5px),radial-gradient(circle_at_65%_75%,white_0.5px,transparent_0.5px)] [background-size:40px_40px,55px_55px]" />
    </div>
  );
}

/**
 * Silueta de hoplita: casco de perfil con penacho, lanza y escudo.
 *
 * De perfil y no de frente a propósito. De frente, un casco corintio se lee
 * como una máscara —lo comprobé tres veces al hacer la animación de entrada—;
 * de perfil, el penacho y el guardanariz lo delatan al instante. Y a esta
 * opacidad solo cuenta la silueta.
 */
function HopliteSilhouette({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 400 700" className={className} fill="rgb(var(--cp-ink))">
      {/* Penacho, barrido hacia atrás */}
      <path d="M196 60 C238 78, 258 128, 250 186 C236 158, 218 138, 196 130 Z" />
      <path d="M188 66 C150 92, 138 140, 148 190 C160 160, 172 138, 190 128 Z" opacity="0.8" />

      {/* Casco de perfil, mirando a la izquierda */}
      <path
        d="M188 118 C232 118, 258 148, 258 190 C258 214, 252 232, 242 244
           L252 268 C232 278, 208 280, 192 274
           C176 268, 164 250, 160 230
           L146 226 L152 206 L142 200 L156 186
           C156 148, 160 118, 188 118 Z"
      />
      {/* Hueco ocular: el único vacío, y basta para que se lea */}
      <path d="M168 176 C180 168, 196 170, 202 180 C194 190, 176 190, 168 182 Z" fill="rgb(var(--cp-bg))" />

      {/* Torso */}
      <path d="M186 278 C150 292, 128 322, 124 366 C122 430, 132 500, 146 560 L252 560 C266 500, 274 430, 272 366 C268 322, 246 292, 210 278 Z" />

      {/* Escudo, tres cuartos */}
      <ellipse cx="118" cy="404" rx="66" ry="86" />
      <ellipse cx="118" cy="404" rx="52" ry="70" fill="rgb(var(--cp-bg))" opacity="0.35" />

      {/* Lanza */}
      <rect x="296" y="90" width="9" height="560" rx="4" />
      <path d="M300 90 l14-46 -28 0z" />

      {/* Piernas */}
      <path d="M156 560 L150 690 L184 690 L192 566 Z" />
      <path d="M242 560 L252 690 L218 690 L208 566 Z" />
    </svg>
  );
}

/** Brasas del fondo: posiciones fijas, para que no salten en cada render. */
const EMBERS = [
  { left: 8, size: 3, delay: 0, duration: 15000 },
  { left: 21, size: 2, delay: 3200, duration: 18000 },
  { left: 34, size: 4, delay: 6400, duration: 13000 },
  { left: 47, size: 2, delay: 1800, duration: 20000 },
  { left: 59, size: 3, delay: 9000, duration: 16000 },
  { left: 72, size: 2, delay: 4600, duration: 17500 },
  { left: 84, size: 4, delay: 11000, duration: 14000 },
  { left: 93, size: 2, delay: 7400, duration: 19000 },
];
